import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Eye, FileCode2, FileMinus2, FileText, Loader2, Mail, Printer, Send } from 'lucide-react';
import { api, type Pagina } from '@/shared/api/cliente';
import { ErrorDeApi } from '@/shared/api/problema';
import { BotonDeFila } from '@/shared/ui/BotonDeFila';
import { CampoDeBusqueda } from '@/shared/ui/CampoDeBusqueda';
import { Modal } from '@/shared/ui/Modal';
import { Tabla, type Columna } from '@/shared/ui/Tabla';
import { VistaPreviaDeImpresion } from '@/shared/ui/VistaPreviaDeImpresion';
import { notificar } from '@/shared/ui/avisos';
import { useValorDiferido } from '@/shared/ui/useValorDiferido';
import { useSesion } from '@/shared/sesion/SesionProvider';
import { DialogoDeNotaDeCredito } from '@/modules/facturacion/DialogoDeNotaDeCredito';
import { PanelDeLaFactura } from '@/modules/facturacion/PanelDeLaFactura';
import { useComprobante } from '@/modules/facturacion/useComprobante';
import {
  SITUACIONES,
  colorDelEstado,
  colorDeLaSituacion,
  type FacturaListada,
} from '@/modules/facturacion/estadosDeFactura';

export function FacturasPorOrigen({
  origen,
  periodo = '',
  vacio,
  situacionInicial = 'autorizada',
}: {
  origen: 'TITULOS' | 'GENERADORES' | 'CARGOS' | 'SIRE';
  periodo?: string;
  vacio: string;
  situacionInicial?: string;
}) {
  // Las del SIRE las autorizó el SRI, pero no todas traen su XML: solo las de agosto de 2024 en
  // adelante, que la migración recupera del SIRE (y entonces llegan con su clave de acceso).
  // Sin él no hay RIDE que abrir ni comprobante que reenviar. La nota de crédito espera a que
  // EPAGAL diga quién la emite.
  const delSire = origen === 'SIRE';
  const sinComprobante = (f: FacturaListada) => delSire && !f.claveAcceso;
  const [buscar, setBuscar] = useState('');
  const [situacion, setSituacion] = useState(situacionInicial);
  const [pagina, setPagina] = useState(0);
  const [tamanio, setTamanio] = useState(25);
  const [abierta, setAbierta] = useState<FacturaListada | null>(null);
  const [acreditando, setAcreditando] = useState<FacturaListada | null>(null);
  const [reenviando, setReenviando] = useState<FacturaListada | null>(null);
  const comprobante = useComprobante();
  const { puede } = useSesion();

  const buscado = useValorDiferido(buscar.trim());

  const enviar = useMutation({
    mutationFn: (f: FacturaListada) =>
      api.post<{ numero: string | null; situacion: string; mensaje: string | null }>(
        `/facturas/${f.id}/emitir`,
        undefined,

        { query: { puntoEmision: 0 } },
      ),
    onSuccess: (r) => {
      const texto = `${r.numero ?? ''} · ${r.situacion.toUpperCase()}`;
      if (r.situacion === 'autorizada') notificar.exito(texto, 'Autorizada por el SRI');
      else
        notificar.advertencia(`${texto}${r.mensaje ? ` · ${r.mensaje}` : ''}`, 'Respuesta del SRI');
      void facturas.refetch();
    },
    onError: (causa) =>
      notificar.error(causa instanceof ErrorDeApi ? causa.message : 'NO SE PUDO ENVIAR AL SRI'),
  });

  const facturas = useQuery({
    queryKey: ['facturas', origen, periodo, buscado, situacion, pagina, tamanio],
    queryFn: () =>
      api.get<Pagina<FacturaListada>>('/facturas', {
        query: { buscar: buscado, situacionSri: situacion, origen, periodo, pagina, tamanio },
      }),
    placeholderData: (anterior) => anterior,
  });

  const columnas: Columna<FacturaListada>[] = [
    {
      clave: 'numero',
      encabezado: 'Comprobante',
      contenido: (f) => (
        <span className="block min-w-0">
          {f.numero ? (
            <span className="importe block truncate text-xs font-semibold text-armazon-800">
              {f.numero}
            </span>
          ) : (
            <span className="block text-[11px] uppercase text-armazon-400">Sin numerar</span>
          )}
          <span className="text-[10px] uppercase text-armazon-400">Cobro {f.periodo}</span>
        </span>
      ),
    },
    {
      clave: 'cliente',
      encabezado: 'Quién pagó',
      contenido: (f) => (
        <span className="block min-w-0">
          <span className="block truncate text-xs font-medium uppercase text-armazon-700">
            {f.cliente}
          </span>
          <span className="importe block text-[10px] text-armazon-400">{f.identificacion}</span>
        </span>
      ),
    },
    {
      clave: 'emitida',
      encabezado: 'Emitida',
      contenido: (f) => (
        <span className="importe text-xs text-armazon-600">{enFecha(f.fechaEmision)}</span>
      ),
    },
    {
      clave: 'autorizacion',
      encabezado: 'Autorización SRI',
      contenido: (f) =>
        f.fechaAutorizacion ? (
          <span className="block">
            <span className="importe block text-xs text-armazon-700">
              {f.fechaAutorizacion.slice(0, 10)}
            </span>
            <span className="importe block text-[10px] text-armazon-400">
              {f.fechaAutorizacion.slice(11)}
            </span>
          </span>
        ) : (
          <span className="text-[11px] text-armazon-300">—</span>
        ),
    },
    {
      clave: 'total',
      encabezado: 'Total',
      alinear: 'derecha',
      contenido: (f) => (
        <span className="importe text-xs font-bold text-armazon-900">${f.total.toFixed(2)}</span>
      ),
    },
    {
      clave: 'estado',
      encabezado: 'Cobro',
      alinear: 'centro',
      contenido: (f) => (
        <span
          className={`rounded-md px-2 py-0.5 text-[10px] font-bold uppercase ${colorDelEstado(f.estado)}`}
        >
          {f.estado}
        </span>
      ),
    },
    {
      clave: 'sri',
      encabezado: 'Ante el SRI',
      alinear: 'centro',
      contenido: (f) => (
        <span className="block">
          <span
            className={`text-[10px] font-bold uppercase ${colorDeLaSituacion(f.situacionSri)}`}
            title={f.claveAcceso ?? undefined}
          >
            {f.situacionSri.replace(/_/g, ' ')}
          </span>
          {f.acreditado > 0 && (
            <span className="mt-0.5 block text-[10px] uppercase text-amber-700">
              {f.tieneNotaTotal ? 'Nota de crédito' : `Acreditado $${f.acreditado.toFixed(2)}`}
            </span>
          )}
        </span>
      ),
    },
    {
      clave: 'acciones',
      encabezado: '',
      alinear: 'derecha',
      clase: 'w-36',
      contenido: (f) => {
        const autorizada = f.situacionSri === 'autorizada' && !sinComprobante(f);
        return (
          <span className="flex justify-end gap-1">
            <BotonDeFila icono={Eye} titulo="Ver la factura" alPulsar={() => setAbierta(f)} />

            {origen === 'TITULOS' &&
              f.situacionSri === 'interna' &&
              f.estado !== 'anulada' &&
              puede('facturacion_electronica.procesar') && (
                <BotonDeFila
                  icono={Send}
                  titulo="Enviar al SRI"
                  alPulsar={() => enviar.mutate(f)}
                  ocupado={enviar.isPending && enviar.variables?.id === f.id}
                />
              )}
            {f.situacionSri !== 'interna' && !sinComprobante(f) && (
              <>
                <BotonDeFila
                  icono={Printer}
                  titulo="Abrir el RIDE"
                  alPulsar={() => void comprobante.abrirRide(f.id)}
                  ocupado={comprobante.ocupado === f.id}
                />
                <BotonDeFila
                  icono={FileCode2}
                  titulo="Descargar el XML autorizado"
                  alPulsar={() => void comprobante.descargarXml(f.id, f.numero)}
                  ocupado={comprobante.ocupado === f.id}
                />
              </>
            )}
            {autorizada && (
              <BotonDeFila
                icono={Mail}
                titulo="Volver a mandar el correo"
                alPulsar={() => setReenviando(f)}
              />
            )}
            {autorizada && !delSire && puede('facturas.anular') && (
              <BotonDeFila
                icono={FileMinus2}
                titulo={f.tieneNotaTotal ? 'Ya tiene una nota de crédito total' : 'Nota de crédito'}
                alPulsar={() => setAcreditando(f)}
                ocupado={f.tieneNotaTotal}
              />
            )}
          </span>
        );
      },
    },
  ];

  return (
    <>
      <Tabla
        datos={facturas.data}
        cargando={facturas.isPending}
        columnas={columnas}
        tamanio={tamanio}
        alCambiarPagina={setPagina}
        alCambiarTamanio={(t) => {
          setTamanio(t);
          setPagina(0);
        }}
        entidad="facturas"
        iconoVacio={FileText}
        mensajeVacio={vacio}
        herramientas={
          <span className="flex flex-wrap items-center gap-2">
            <CampoDeBusqueda
              valor={buscar}
              alCambiar={(v) => {
                setBuscar(v);
                setPagina(0);
              }}
              marcador="Número, clave, cédula o nombre…"
              className="w-72"
            />
            <select
              value={situacion}
              onChange={(e) => {
                setSituacion(e.target.value);
                setPagina(0);
              }}
              aria-label="Ante el SRI"
              className="h-9 rounded-lg border border-armazon-200 bg-white px-2 text-xs uppercase text-armazon-600 outline-none transition-colors focus:border-marca-400"
            >
              <option value="">ANTE EL SRI: TODAS</option>
              {SITUACIONES.map((s) => (
                <option key={s.valor} value={s.valor}>
                  {s.etiqueta.toLocaleUpperCase('es')}
                </option>
              ))}
            </select>
          </span>
        }
      />

      <VistaPreviaDeImpresion
        abierto={comprobante.ride !== null || comprobante.ocupado !== null}
        alCerrar={comprobante.cerrar}
        titulo="RIDE"
        subtitulo="Representación impresa del comprobante electrónico"
        fichero={comprobante.ride}
        cargando={comprobante.ocupado !== null}
        formato="hoja"
        textoDeCarga="Preparando el RIDE…"
      />

      {abierta && <PanelDeLaFactura factura={abierta} abierto alCerrar={() => setAbierta(null)} />}

      <DialogoDeNotaDeCredito
        abierto={acreditando !== null}
        factura={acreditando}
        alCerrar={() => setAcreditando(null)}
        alEmitir={() => void facturas.refetch()}
      />

      {reenviando && <ReenvioDelCorreo factura={reenviando} alCerrar={() => setReenviando(null)} />}
    </>
  );
}

function ReenvioDelCorreo({
  factura,
  alCerrar,
}: {
  factura: FacturaListada;
  alCerrar: () => void;
}) {
  const [destino, setDestino] = useState('');

  const enviar = useMutation({
    mutationFn: () =>
      api.post<{ destino: string }>(`/facturas/${factura.id}/correo`, undefined, {
        query: { destino: destino.trim() },
      }),
    onSuccess: (r) => {
      notificar.exito(`Comprobante ${factura.numero ?? ''} enviado a ${r.destino}`);
      alCerrar();
    },
    onError: (causa) =>
      notificar.error(causa instanceof ErrorDeApi ? causa.message : 'NO SE PUDO ENVIAR'),
  });

  return (
    <Modal
      abierto
      alCerrar={alCerrar}
      titulo="Volver a mandar el comprobante"
      subtitulo={`${factura.numero ?? ''} · ${factura.cliente}`}
      icono={Mail}
      ancho="estrecho"
    >
      <label className="grid gap-1.5">
        <span className="text-[10px] font-semibold uppercase text-armazon-600">A qué correo</span>
        <input
          type="email"
          value={destino}
          onChange={(e) => setDestino(e.target.value)}
          placeholder="Vacío: al correo que tiene registrado"
          autoFocus
          className="h-10 w-full rounded-lg border border-armazon-200 bg-white px-3 text-sm outline-none transition-colors focus:border-marca-400"
        />
        <span className="text-[10px] uppercase text-armazon-400">
          Van el RIDE en PDF y el XML autorizado.
        </span>
      </label>

      <div className="mt-5 flex justify-end gap-2">
        <button
          type="button"
          onClick={alCerrar}
          className="h-10 rounded-lg px-4 text-xs font-semibold uppercase text-armazon-600 hover:bg-armazon-100"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={() => enviar.mutate()}
          disabled={enviar.isPending}
          className="inline-flex h-10 items-center gap-2 rounded-lg bg-marca-600 px-4 text-xs font-bold uppercase text-white hover:bg-marca-700 disabled:opacity-50"
        >
          {enviar.isPending ? (
            <Loader2 className="size-4 animate-spin" aria-hidden />
          ) : (
            <Mail className="size-4" aria-hidden />
          )}
          Enviar
        </button>
      </div>
    </Modal>
  );
}

function enFecha(iso: string): string {
  const [anio, mes, dia] = iso.split('-');
  return `${dia}/${mes}/${anio}`;
}
